import React, { useEffect, useState } from "react";
import { FileText, GraduationCap, AlertCircle, CheckCircle2, XCircle, Clock, Loader2, Trash2 } from "lucide-react";
import { api, fmtDate, openAuthedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import AIInsightsCard from "@/components/AIInsightsCard";
import { PresenceWidget } from "@/components/PresenceWidget";

const STATUS_BADGE = {
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  incomplete: { label: "Incomplet", color: "bg-orange-100 text-orange-800", icon: AlertCircle },
  validated: { label: "Validé", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  waitlisted: { label: "Liste d'attente", color: "bg-blue-100 text-blue-800", icon: Clock },
  rejected: { label: "Rejeté", color: "bg-red-100 text-red-800", icon: XCircle },
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [d, e] = await Promise.all([
        api.get("/dashboard/admin"),
        api.get("/enrollments"),
      ]);
      setStats(d.data);
      setEnrollments(e.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/enrollments/${id}`, { status, reason });
      toast.success(`Statut mis à jour : ${STATUS_BADGE[status].label}`);
      setSelected(null);
      setReason("");
      load();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const purgeAll = async () => {
    if (!window.confirm(`Annuler et supprimer DÉFINITIVEMENT les ${enrollments.length} dossiers d'inscription ?\n(Les fiches élèves déjà créées ne sont pas touchées.)`)) return;
    try {
      const { data } = await api.post("/enrollments/purge-all");
      toast.success(`${data.deleted} dossier(s) supprimé(s)`);
      load();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <div className="chip mb-2"><FileText className="h-3 w-3" /><span>Administration</span></div>
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] tracking-tight">Gestion administrative</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="text-xs uppercase tracking-wider text-slate-500 mb-2">En attente</div><div className="font-heading font-extrabold text-2xl text-yellow-600">{stats?.enrollments_pending}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Validés</div><div className="font-heading font-extrabold text-2xl text-[#166534]">{stats?.enrollments_validated}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Élèves inscrits</div><div className="font-heading font-extrabold text-2xl text-[#1E3A8A]">{stats?.students_total}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Réclamations actives</div><div className="font-heading font-extrabold text-2xl text-[#EA580C]">{(stats?.complaints_new || 0) + (stats?.complaints_in_progress || 0)}</div></div>
      </div>

      {/* AI Insights — priorités admin du jour */}
      <PresenceWidget />

      <AIInsightsCard accentColor="#1E3A8A" />

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-heading font-bold text-lg text-[#1E3A8A]">Dossiers d&apos;inscription</h3>
            <p className="text-xs text-slate-500">{enrollments.length} dossiers</p>
          </div>
          {enrollments.length > 0 && (
            <Button size="sm" variant="outline" onClick={purgeAll}
              className="border-red-300 text-red-700 hover:bg-red-50 gap-1.5 text-xs"
              data-testid="enrollments-purge-all-btn">
              <Trash2 className="h-3.5 w-3.5" /> Vider les dossiers
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3 text-left">Réf.</th>
                <th className="px-5 py-3 text-left">Date</th>
                <th className="px-5 py-3 text-left">Enfant</th>
                <th className="px-5 py-3 text-left">Niveau</th>
                <th className="px-5 py-3 text-left">Parent</th>
                <th className="px-5 py-3 text-center">Statut</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {enrollments.map((e) => {
                const Status = STATUS_BADGE[e.status];
                return (
                  <tr key={e.id} className="hover:bg-slate-50" data-testid={`enrollment-row-${e.id}`}>
                    <td className="px-5 py-4 text-xs font-mono font-bold text-[#1E3A8A]" data-testid={`enrollment-ref-${e.id}`}>{e.id.slice(0, 8).toUpperCase()}</td>
                    <td className="px-5 py-4 text-sm text-slate-600">{fmtDate(e.created_at)}</td>
                    <td className="px-5 py-4 text-sm font-semibold">{e.child_first_name} {e.child_last_name}</td>
                    <td className="px-5 py-4 text-sm text-slate-600">{e.requested_level}</td>
                    <td className="px-5 py-4 text-sm">
                      <div className="text-[#1E3A8A] font-semibold">{e.parent_name}</div>
                      <div className="text-xs text-slate-500">{e.parent_phone || e.parent_email}</div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${Status.color}`}>
                        <Status.icon className="h-3 w-3" /> {Status.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Dialog open={selected === e.id} onOpenChange={(o) => { setSelected(o ? e.id : null); setReason(""); }}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" className="text-xs" data-testid={`enrollment-action-${e.id}`}>Traiter</Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Dossier de {e.child_first_name} {e.child_last_name}</DialogTitle></DialogHeader>
                          <div className="space-y-3 text-sm">
                            <div><span className="text-slate-500">Niveau demandé:</span> <strong>{e.requested_level}</strong></div>
                            <div><span className="text-slate-500">Date de naissance:</span> {e.child_birth_date}</div>
                            <div><span className="text-slate-500">Ancienne école:</span> {e.previous_school || "—"}</div>
                            <div>
                              <span className="text-slate-500">Documents joints par le parent:</span>
                              {(e.documents_files || []).length > 0 ? (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {e.documents_files.map(df => (
                                    <button key={df.id} type="button"
                                      onClick={() => openAuthedFile(`/enrollments/${e.id}/documents/${df.id}/file`).catch(() => toast.error("Fichier indisponible"))}
                                      className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg bg-blue-50 text-[#1E3A8A] border border-blue-200 hover:bg-blue-100"
                                      data-testid={`enrollment-doc-${df.id}`}>
                                      📄 {df.doc_name}
                                    </button>
                                  ))}
                                </div>
                              ) : <span> {(e.documents || []).join(", ") || "Aucun"}</span>}
                            </div>
                            <div><span className="text-slate-500">Infos médicales:</span> {e.medical_info || "—"}</div>
                            <Textarea placeholder="Motif (optionnel)" value={reason} onChange={(ev) => setReason(ev.target.value)} data-testid="enrollment-reason" />
                            <div className="grid grid-cols-2 gap-2 pt-2">
                              <Button onClick={() => updateStatus(e.id, "validated")} className="bg-[#166534] text-white gap-1" data-testid="enrollment-validate"><CheckCircle2 className="h-4 w-4"/> Valider</Button>
                              <Button onClick={() => updateStatus(e.id, "incomplete")} variant="outline" className="border-orange-500 text-orange-700 gap-1" data-testid="enrollment-incomplete"><AlertCircle className="h-4 w-4"/> Incomplet</Button>
                              <Button onClick={() => updateStatus(e.id, "rejected")} variant="outline" className="border-red-500 text-red-700 gap-1 col-span-2" data-testid="enrollment-reject"><XCircle className="h-4 w-4"/> Rejeter</Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </td>
                  </tr>
                );
              })}
              {enrollments.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">Aucune inscription en cours</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
